import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Cropper, { type Area } from 'react-easy-crop';
import { X, ZoomIn, ZoomOut } from 'lucide-react';
import { cn } from '../../lib/cn';

interface AvatarCropModalProps {
  imageUrl: string;
  onCropComplete: (blob: Blob) => void;
  onCancel: () => void;
}

async function getCroppedImg(imageSrc: string, pixelCrop: Area, outputSize = 256): Promise<Blob | null> {
  const image = new Image();
  image.src = imageSrc;
  await new Promise((resolve) => { image.onload = resolve; });

  const canvas = document.createElement('canvas');
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, outputSize, outputSize);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
  });
}

export function AvatarCropModal({ imageUrl, onCropComplete, onCancel }: AvatarCropModalProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropCompleteCallback = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  async function handleConfirm() {
    if (!croppedAreaPixels) return;
    setIsProcessing(true);
    const blob = await getCroppedImg(imageUrl, croppedAreaPixels);
    if (blob) onCropComplete(blob);
    setIsProcessing(false);
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="relative z-10 bg-cime-surface border border-cime-border rounded-xl w-[440px] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-cime-border">
          <h3 className="font-heading text-lg font-semibold text-cime-text-primary">
            Recadrer la photo
          </h3>
          <button
            type="button"
            onClick={onCancel}
            className="p-1 rounded hover:bg-cime-primary/10 text-cime-text-muted hover:text-cime-primary transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Cropper */}
        <div className="relative h-72 bg-black">
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropCompleteCallback}
          />
        </div>

        {/* Zoom slider */}
        <div className="px-5 py-4 border-t border-cime-border">
          <div className="flex items-center gap-3">
            <ZoomOut className="w-4 h-4 text-cime-text-muted flex-shrink-0" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className={cn(
                'flex-1 h-px rounded-full appearance-none cursor-pointer',
                'bg-neutral-400',
                '[&::-webkit-slider-thumb]:appearance-none',
                '[&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4',
                '[&::-webkit-slider-thumb]:rounded-full',
                '[&::-webkit-slider-thumb]:bg-cime-primary',
                '[&::-webkit-slider-thumb]:cursor-pointer',
                '[&::-webkit-slider-thumb]:transition-opacity',
                '[&::-webkit-slider-thumb]:hover:opacity-80',
              )}
            />
            <ZoomIn className="w-4 h-4 text-cime-text-muted flex-shrink-0" />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-5 py-4 border-t border-cime-border">
          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            className="flex-1 px-4 py-2 text-sm rounded-lg border border-cime-border text-cime-text-muted hover:bg-cime-primary/5 transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isProcessing}
            className="flex-1 px-4 py-2 text-sm rounded-lg font-medium bg-cime-primary text-white hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {isProcessing ? 'Traitement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
